import React from 'react';
import ReactDOM from 'react-dom';
import { useEditor, useNode } from '@craftjs/core';
import { useSelector } from 'react-redux';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import styles from './styles/styles.module.css';
import { useInteractionMode } from '../contexts/InteractionMode';
import moment from 'moment';

/** Portal so the modal sits above all stacking contexts */
function BodyPortal({ children }: { children: React.ReactNode }) {
  const el = React.useMemo(() => document.createElement('div'), []);
  React.useEffect(() => {
    document.body.appendChild(el);
    return () => {
      document.body.removeChild(el);
    };
  }, [el]);
  return ReactDOM.createPortal(children, el);
}

const DatePickerField = ({
  label,
  selectedDate,
  value,
  isCreationDate = false,
  required = false, // builder-time default
  left = 0,
  top = 0,
  width = 200,
  height = 40,
}: any) => {
  const { enabled, actions } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  const {
    connectors: { connect },
    setProp,
    id,
  } = useNode();

  const interactionMode = useInteractionMode();
  const { selected } = useSelector((s: any) => s.formToggle);
  const tz = useSelector((state: any) => state.currentSite.timezone);

  const [isDragging, setDragging] = React.useState(false);
  const [showEditor, setShowEditor] = React.useState(false);

  // Close modal on Escape
  React.useEffect(() => {
    if (!showEditor) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowEditor(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showEditor]);

  // -------- interactions --------
  const onMouseDown = (e: React.MouseEvent) => {
    if (!enabled || showEditor) return;
    e.preventDefault();
    setDragging(true);

    const startX = e.clientX;
    const startY = e.clientY;
    const initialLeft = left;
    const initialTop = top;

    const onMouseMove = (ev: MouseEvent) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      setProp((props: any) => {
        props.left = initialLeft + dx;
        props.top = initialTop + dy;
      });
    };

    const onMouseUp = () => {
      setDragging(false);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const onDoubleClick = () => {
    if (!enabled) return;
    if (selected === 'FORM') setShowEditor(true); // editor only in FORM
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (selected === 'FORM') actions.delete(id);
  };

  // -------- classes --------
  function classes() {
    if (selected === 'REPORT') {
      return {
        wrapper: styles.wrapperReport,
        label: styles.labelReport,
        inputContainer: styles.inputContainerReport,
        dateInput: styles.dateInputReport,
        row: styles.rowReport,
        value: styles.valueReport,
      };
    }
    return {
      wrapper: styles.wrapperForm,
      label: styles.labelForm,
      inputContainer: styles.inputContainerForm,
      dateInput: styles.dateInputForm,
      row: styles.rowReport, // unused in FORM
      value: styles.valueReport, // unused in FORM
    };
  }

  const cls = classes();

  const d = selectedDate ? new Date(selectedDate) : null;
  const mm = d ? String(d.getMonth() + 1).padStart(2, '0') : 'MM';
  const dd = d ? String(d.getDate()).padStart(2, '0') : 'DD';
  const yyyy = d ? String(d.getFullYear()) : 'YYYY';
  const inlineDate = `${mm}/${dd}/${yyyy}`;

  const isView = interactionMode === 'VIEW';
  const isForm = selected === 'FORM';
  const isFill = interactionMode === 'FILL';

  return (
    <>
      <div
        ref={(ref) => ref && connect(ref)}
        className={cls.wrapper}
        style={{
          left,
          top,
          position: 'absolute',
          cursor: enabled ? (isDragging ? 'grabbing' : 'grab') : 'default',
        }}
        onMouseDown={onMouseDown}
        onDoubleClick={onDoubleClick}
        role={enabled ? 'button' : undefined}
        tabIndex={enabled ? 0 : undefined}
        data-required={String(!!required)}
      >
        {/* REPORT view */}
        {selected === 'REPORT' ? (
          <div className={cls.row} style={{ width, height }}>
            <span className={cls.value}>{inlineDate}</span>
          </div>
        ) : (
          <>
            {/* VIEW */}
            {isView && (
              <div className={styles.viewDateContainer}>
                <p className={styles.viewLabel}>
                  {label}
                  {required && (
                    <span style={{ marginLeft: 6, color: '#c00' }} aria-hidden>
                      *
                    </span>
                  )}
                </p>
                <p className={styles.viewDate}>
                  {value ? moment(value).tz(tz).format('MM.DD.YYYY') : '—'}
                </p>
              </div>
            )}

            {/* FORM/FILL label */}
            {!isView && (
              <div className={cls.label}>
                {label}
                {required && (
                  <span style={{ marginLeft: 6, color: '#c00' }} aria-hidden>
                    *
                  </span>
                )}
                {enabled && (
                  <div
                    className={styles.deleteButton}
                    onClick={handleDelete}
                    onDoubleClick={(e) => e.stopPropagation()}
                    aria-label="Delete field"
                  >
                    ×
                  </div>
                )}
              </div>
            )}

            {/* FORM/FILL input */}
            {!isView && (
              <div
                className={cls.inputContainer}
                style={{ width, height, overflow: 'hidden' }}
              >
                {enabled ? (
                  // Fake display while designing
                  <div className={styles.fakeDateDisplay}>
                    <div className={styles.dateBox}>{mm}</div>
                    <div className={styles.slash}>/</div>
                    <div className={styles.dateBox}>{dd}</div>
                    <div className={styles.slash}>/</div>
                    <div className={styles.dateBox}>{yyyy}</div>
                  </div>
                ) : (
                  <DatePicker
                    selected={d}
                    onChange={(date) =>
                      setProp((props: any) => {
                        props.selectedDate = date?.toISOString() || '';
                      })
                    }
                    className={cls.dateInput}
                    placeholderText="Select date"
                    dateFormat="MM/dd/yyyy"
                    popperPlacement="bottom-start"
                    popperClassName="custom-datepicker-popper"
                    portalId="root-portal"
                    required={isFill ? !!required : false}
                  />
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* === Centered modal editor (FORM only) === */}
      {showEditor && isForm && (
        <BodyPortal>
          <div
            className={styles.modalOverlay}
            role="presentation"
            onMouseDown={() => setShowEditor(false)} /* click backdrop closes */
          >
            <div
              className={styles.modalPanel}
              role="dialog"
              aria-modal="true"
              aria-label="Edit date field"
              onMouseDown={(e) => e.stopPropagation()} /* keep clicks inside */
            >
              <div className={styles.modalHeader}>
                <h3 className={styles.modalTitle}>Edit Date Field</h3>
                <button
                  className={styles.modalClose}
                  aria-label="Close"
                  onClick={() => setShowEditor(false)}
                >
                  ×
                </button>
              </div>

              <div className={styles.modalBody}>
                <div className={styles.formRow}>
                  <label className={styles.rowLabel}>Label</label>
                  <input
                    className={styles.rowInput}
                    type="text"
                    value={label}
                    onChange={(e) =>
                      setProp((props: any) => (props.label = e.target.value))
                    }
                  />
                </div>

                <div className={styles.formRowCheck}>
                  <input
                    type="checkbox"
                    checked={!!isCreationDate}
                    onChange={(e) =>
                      setProp((props: any) => {
                        props.isCreationDate = e.target.checked;
                      })
                    }
                  />
                  <label style={{ marginLeft: 6 }} htmlFor={`required-${id}`}>
                    Creation Date
                  </label>
                </div>

                {/* New: builder-time required toggle */}
                <div
                  className={styles.formRowInline}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <input
                    id={`required-${id}`}
                    type="checkbox"
                    checked={!!required}
                    onChange={(e) =>
                      setProp((props: any) => {
                        props.required = e.target.checked;
                      })
                    }
                  />
                  <label style={{ marginLeft: 6 }} htmlFor={`required-${id}`}>
                    Required field
                  </label>
                </div>
              </div>

              <div className={styles.modalFooter}>
                <button
                  className={styles.btnPrimary}
                  onClick={() => setShowEditor(false)}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </BodyPortal>
      )}
    </>
  );
};

(DatePickerField as any).craft = {
  displayName: 'DatePickerField',
  props: {
    label: 'Select a date',
    selectedDate: '',
    value: '',
    isCreationDate: false,
    required: false, // default off
    left: 0,
    top: 0,
    width: 200,
    height: 40,
  },
};

export default DatePickerField;
